'use client'

import { getValidatedAccessToken } from '@/lib/supabase-validated-session'
import { parseAccountSnapshot, type AccountSnapshot } from '@/lib/account-snapshot'
import type { PreferredLanguage } from '@/server/domain/language'

/** Lo que el cliente puede cambiar de su cuenta. `PUT /api/account` devuelve la cuenta entera. */
export interface AccountPatch {
  preferredLanguage?: PreferredLanguage
  marketingConsent?: boolean
  /** El PIN de salida del modo niño (4 dígitos). La cuenta devuelta solo dice si hay uno. */
  pin?: string
}

async function requireCurrentAccessToken(): Promise<string> {
  const accessToken = await getValidatedAccessToken()
  if (!accessToken) {
    throw new Error('No authentication token available')
  }
  return accessToken
}

async function readAccountResponse(response: Response, fallbackMessage: string): Promise<AccountSnapshot> {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error?.message || fallbackMessage)
  }
  return parseAccountSnapshot(await response.json())
}

async function fetchAccount(accessToken: string): Promise<AccountSnapshot> {
  const response = await fetch('/api/account', {
    headers: { Authorization: `Bearer ${accessToken}` }
  })
  return readAccountResponse(response, 'Failed to refresh account data')
}

export async function getAccount(): Promise<AccountSnapshot> {
  return fetchAccount(await requireCurrentAccessToken())
}

/**
 * Para las pantallas que también se ven sin sesión: sin ella devuelve `null`
 * (un estado esperado, no un fallo) y solo lanza ante fallos de verdad.
 */
export async function getAccountIfSignedIn(): Promise<AccountSnapshot | null> {
  const accessToken = await getValidatedAccessToken()
  return accessToken ? fetchAccount(accessToken) : null
}

/** Aplica `patch` y devuelve la cuenta tal y como queda en el servidor. */
export async function putAccount(patch: AccountPatch): Promise<AccountSnapshot> {
  const accessToken = await requireCurrentAccessToken()
  const response = await fetch('/api/account', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify(patch)
  })
  return readAccountResponse(response, 'Failed to update account')
}
